import { ProgressBar } from "@kensho/neo";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { useAPIFetch } from "../../api/use-api-fetch";
import { useGetTranscriptMetadata } from "../../api/use-get-transcript-metadata";
import { ErrorDialog } from "../../components/error-dialog";
import { MultiplayerProvider } from "../../providers/multiplayer-provider";
import { SpellCheckProvider } from "../../providers/spell-check-provider";
import { TranscriptProvider } from "../../providers/transcript-provider";
import { TranscriptionProvider } from "../../providers/transcription-provider";
import { APITranscript, TranscriptionConfiguration } from "../../types/types";
import { camelCaseDeep } from "../../utils/camel-case-deep";
import { parseError } from "../../utils/parse-error";
import { TranscriptPermissionsProvider } from "./transcript-permissions-provider";
import { Transcription } from "./transcription";

interface Props {
  transcriptId: string;
  mediaFile?: File;
  setMediaFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  transcriptionConfiguration: TranscriptionConfiguration;
  setTranscriptionConfiguration: React.Dispatch<
    React.SetStateAction<TranscriptionConfiguration>
  >;
  reset: () => void;
}

export function TranscriptionWithContext({
  transcriptId,
  mediaFile,
  setMediaFile,
  transcriptionConfiguration,
  setTranscriptionConfiguration,
  reset,
}: Props) {
  const getTranscriptMetadata = useGetTranscriptMetadata();
  const { data: metadata, error: metadataError } = useQuery({
    queryKey: ["transcriptMetadata", transcriptId],
    queryFn: async ({ signal }) => getTranscriptMetadata(transcriptId, signal),
    gcTime: 0,
    refetchInterval: ({ state: { data, status } }) => {
      const POLL_INTERVAL = 5000;
      if (status === "error") return false;
      if (status === "pending") return POLL_INTERVAL;
      if (status === "success") {
        if (!data) return false;
        const { status: transcriptStatus } = data;
        if (["complete", "failed"].includes(transcriptStatus)) return false;
        if (["in_progress", "submitted"].includes(transcriptStatus))
          return POLL_INTERVAL;
      }
      return false;
    },
    retry: false,
  });

  const apiFetch = useAPIFetch();
  const { data, error: transcriptError } = useQuery({
    queryKey: ["transcript", transcriptId],
    queryFn: async () => {
      const response = await apiFetch.execute(
        `/app/v2/transcription/${transcriptId}`,
        {},
      );
      if (response.status === 202) {
        throw new Error("Transcription is still in progress");
      }
      if (!response.ok) {
        throw new Error("Failed to fetch transcript ");
      }
      return camelCaseDeep(await response.json()) as {
        transcript: APITranscript;
        version: number;
      };
    },
    enabled: metadata?.status === "complete",
    gcTime: 0,
    retry: false,
  });

  const { transcript, version } =
    data && "version" in data
      ? { transcript: data.transcript, version: data.version }
      : { transcript: data, version: undefined };

  const navigate = useNavigate();

  if (metadataError) {
    return (
      <ErrorDialog
        isOpen={!!metadataError}
        error={parseError(metadataError)}
        onClose={() => navigate("/transcriptions")}
      />
    );
  }

  if (metadata && metadata.status === "failed") {
    return (
      <ErrorDialog
        isOpen
        error={{ type: "generic", title: "Transcription Failed" }}
        onClose={() => {
          navigate("/transcriptions");
        }}
      />
    );
  }

  if (transcriptError) {
    return (
      <ErrorDialog
        isOpen={!!transcriptError}
        error={transcriptError ? parseError(transcriptError) : undefined}
        onClose={() => {
          navigate("/transcriptions");
        }}
      />
    );
  }
  if (!transcript) {
    return (
      <div className="mx-auto mt-10 h-full w-[750px] max-w-full overflow-auto px-6 py-5">
        <div className="mt-10">
          <div className="mt-2 mb-3 text-lg">
            {(() => {
              if (!metadata) return "Checking Transcription Status…";
              if (["in_progress", "submitted"].includes(metadata.status))
                return "Transcribing…";
              if (metadata.status === "complete") return "Loading…";
              return null;
            })()}
          </div>
          <ProgressBar />
        </div>
      </div>
    );
  }

  return (
    <TranscriptionProvider
      initialMetadata={metadata}
      initialStage={"POST_TRANSCRIPTION"}
    >
      <TranscriptPermissionsProvider>
        <MultiplayerProvider>
          <TranscriptProvider
            transcriptId={transcriptId}
            initialTranscript={transcript}
            initialVersion={version || 0}
          >
            <SpellCheckProvider>
              <Transcription
                transcriptId={transcriptId}
                mediaFile={mediaFile}
                setMediaFile={setMediaFile}
                transcriptionConfiguration={transcriptionConfiguration}
                setTranscriptionConfiguration={setTranscriptionConfiguration}
                reset={reset}
              />
            </SpellCheckProvider>
          </TranscriptProvider>
        </MultiplayerProvider>
      </TranscriptPermissionsProvider>
    </TranscriptionProvider>
  );
}
